import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Modal, Box, Typography } from '@mui/material';
import CustomIconButton from './CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Refresh.svg';
import arrowUp from 'packages/novous_web/src/assets/icons/ArrowBlack.svg';
import arrowDown from 'packages/novous_web/src/assets/icons/ArrowDownBlack.svg';
import CustomDropDownInput from './customDropDownInput';
import { useAuth } from '../../authContext/useAuth';
import useAxios from '../../api/useAxios';
import { API_ENDPOINTS } from '../../api/endpoints';
import InputField from './InputField';

interface FilterDataModelProps {
  open: boolean;
  onClose: () => void;
  onReset?: () => void;
  title?: string;
  isClear?: boolean;
  subTitle?: string;
  handleFilterSave: (data: any) => void;
  description?: string;
  showTripStatus?: boolean;
  showClearButton?: boolean;
  showGroupFilter?: boolean;
  showAlarmFilter?: boolean;
  showDriverFilter?: boolean;
  showVehicleFilter?: boolean;

  showCisNumberFilter?: boolean;
  showOprsNumberFilter?: boolean;
  showTripIdFilter?: boolean;
  showProductTypeFilter?: boolean;
  showStageNumberFilter?: boolean;
  showTripStartCityFilter?: boolean;
  showTripEndCityFilter?: boolean;

  onMetaFetched?: (data: { vehicles: any; groups: any }) => void;
  initialFilters?: {
    group?: string | null;
    tripStatus?: string | null;
    vehicle?: string | null;
    alarm?: string | null;
    driver?: string | null;
    cisNumber?: string | null;
    oprsNumber?: string | null;
    tripId?: string | null;
    productType?: string | null;
    stageNumber?: string | null;
    tripStartCity?: string | null;
    tripEndCity?: string | null;
  };
  DriversData?: any;
  trgtcData?: boolean;
}

interface GroupOption {
  code: string;
  label: string;
}

interface DriverOption {
  value: string;
  label: string;
  driverId: string;
}

const FilterDataModel: React.FC<FilterDataModelProps> = ({
  open,
  onClose,
  onReset,
  isClear = false,
  title = 'Customise Table',
  subTitle = '',
  handleFilterSave,
  description = 'Choose which sections to display and arrange them the way you like.',
  showTripStatus = true,
  showClearButton = true,
  showGroupFilter = true,
  showAlarmFilter = false,
  showVehicleFilter = true,
  showDriverFilter = false,

  showCisNumberFilter = false,
  showOprsNumberFilter = false,
  showTripIdFilter = false,
  showProductTypeFilter = false,
  showStageNumberFilter = false,
  showTripStartCityFilter = false,
  showTripEndCityFilter = false,

  onMetaFetched,
  initialFilters,
  DriversData,
  trgtcData = false,
}) => {
  const { user } = useAuth();
  const [clear, setClear] = useState<boolean>(isClear || false);
  const [tripStatusOptions] = useState<GroupOption[]>([
    { code: 'PENDING', label: 'Trip Yet To Start' },
    { code: 'COMPLETED', label: 'Completed' },
    { code: 'CORRUPTED', label: 'Corrupted' },
    { code: 'ONGOING', label: 'Ongoing' },
    { code: 'DEVIATED', label: 'Deviated' },
    { code: 'DELAYED', label: 'Delayed' },
    { code: 'INCOMPLETE', label: 'Incomplete' },
    { code: 'FAILED', label: 'Failed' },
  ]);
  const alarmOptions = [
    'Drowsiness',
    'Distracted',
    'Smoking',
    'Phone Calling',
    'Seat Belt',
    'Yawning',
    'Unauthorized Driver',
    'Camera Block',
    'Forward Collision',
    'Lane Departure',
    'Vulnerable Road User',
    'Potential Collision',
    'Harsh Acceleration',
    'Harsh Braking',
    'Harsh Turning',
    'Over Speeding',
    'Vehicle Idling',
    'Continuous Driving',
    'Night Driving',
    'SOS',
    'Cabin Occupancy',
    'Right Blind Spot',
    'Left Blind Spot',
  ].map((item) => ({ value: item, label: item }));

  useEffect(() => {
    setClear(isClear);
  }, [isClear]);

  useEffect(() => {
    if (clear) {
      setSelectedGroup(null);
      setSelectedTripStatus(null);
      setSelectedVehicle(null);
      setSelectedAlarm(null);
      setSelectedDriver(null);
      setDataTableExpanded(false);
      setTripStatusExpanded(false);
      setVehicleExpanded(false);
      setAlarmExpanded(false);
      setDriverExpanded(false);
      setClear(false);
      onReset?.();
    }
  }, [clear]);

  const [cisNumber, setCisNumber] = useState<string | null>(null);
  const [oprsNumber, setOprsNumber] = useState<string | null>(null);
  const [tripId, setTripId] = useState<string | null>(null);
  const [productType, setProductType] = useState<string | null>(null);
  const [stageNumber, setStageNumber] = useState<string | null>(null);
  const [tripStartCity, setTripStartCity] = useState<string | null>(null);
  const [tripEndCity, setTripEndCity] = useState<string | null>(null);
  //console.log(showClearButton, onReset, clear);
  // Fetch vehicle and group data
  const {
    data: vehicleData,
    error: vehicleError,
    loading: vehicleLoading,
    fetchData: fetchVehicleData,
  } = useAxios<any>(`${API_ENDPOINTS.dashboard.VEHICLE}`, 'POST');
  const {
    data: groupsData,
    error: groupsError,
    loading: groupsLoading,
    fetchData: fetchGroupsData,
  } = useAxios<{ data: any }>(`${API_ENDPOINTS.dashboard.GROUP}${user?.customer_id}`, 'GET');

  const { data: vehicleOrDriverData, fetchData: fetchVehicleOrDriverData } = useAxios<{ data: any }>(
    API_ENDPOINTS.alarm.DRIVER_ALERT_BY_DRIVER_OR_VEHICLE,
    'POST',
  );

  let vehicleOptions: any[] = [];
  let driverOptions: DriverOption[] = [];

  if (vehicleData) {
    vehicleOptions = vehicleData.map((item: any) => {
      return {
        code: item.vehicleRegistrationNo,
        groupName: item.groupName || item.group || null,
        deviceId: item.deviceId,
        vehicleName: item.vehicleRegistrationNo,
        label: item.vehicleRegistrationNo,
      };
    });
  }

  if (vehicleOrDriverData?.data?.drivers?.length) {
    driverOptions = vehicleOrDriverData.data.drivers.map((item: any) => ({
      value: item.key,
      label: `${item.value} (${item.employeeId || ''})`.trim(),
      driverId: item.key,
    }));
  } else if (DriversData) {
    driverOptions = DriversData.map((driver: any) => ({
      value: driver.driverId,
      label: `${driver.driverName} (${driver.empId || ''})`.trim(),
      driverId: driver.driverId,
    }));
  }

  useEffect(() => {
    if (user?.customer_id) {
      fetchVehicleData({ fleetId: user?.customer_id });
      fetchGroupsData();
      if (showDriverFilter) {
        fetchVehicleOrDriverData({ fleetId: user?.customer_id });
      }
    }
  }, [user?.customer_id]);

  const metaData = useMemo(() => {
    if (!vehicleData || !groupsData || !DriversData) return null;

    return {
      vehicles: vehicleData.map((item: any) => item.vehicleRegistrationNo),
      groups: groupsData?.data || [],
      drivers: DriversData || [],
    };
  }, [vehicleData, groupsData]);
  useEffect(() => {
    if (metaData && typeof onMetaFetched === 'function') {
      const currentMetaString = JSON.stringify(metaData);
      if (lastSentMetaRef.current !== currentMetaString) {
        console.log('Sending metaData to parent', metaData);
        lastSentMetaRef.current = currentMetaString;
        onMetaFetched(metaData);
      }
    }
  }, [metaData, onMetaFetched]);

  // useEffect(() => {
  //     if (vehicleData && groupsData && typeof onMetaFetched === 'function') {
  //         onMetaFetched({
  //             vehicles: vehicleData.map((item: any) =>
  //                 item.vehicleRegistrationNo,
  //             ),
  //             groups: groupsData?.data || [],
  //         });
  //     }
  // }, [vehicleData, groupsData, onMetaFetched]);
  let groupOptions: GroupOption[] = [];

  if (groupsData && groupsData?.data) {
    groupOptions = groupsData?.data.map((item: string) => {
      return { code: item, label: item };
    });
  }

  const lastSentMetaRef = useRef<string | null>(null);

  // Selections
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedTripStatus, setSelectedTripStatus] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [selectedAlarm, setSelectedAlarm] = useState<string | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);

  // Vehicles to show depending on selected group/depot
  const displayedVehicleOptions = useMemo(() => {
    if (!selectedGroup) return vehicleOptions;
    return vehicleOptions.filter((v) => v.groupName === selectedGroup);
  }, [vehicleOptions, selectedGroup]);

  // If the selected vehicle doesn't belong to the chosen group, clear it.
  useEffect(() => {
    if (!selectedGroup) return;
    if (selectedVehicle) {
      const exists = displayedVehicleOptions.some((v) => v.code === selectedVehicle);
      if (!exists) setSelectedVehicle(null);
    }
  }, [selectedGroup, displayedVehicleOptions]);
  // Expanded/collapsed states
  const [dataTableExpanded, setDataTableExpanded] = useState<boolean>(false);
  const [tripStatusExpanded, setTripStatusExpanded] = useState<boolean>(false);
  const [vehicleExpanded, setVehicleExpanded] = useState<boolean>(false);
  const [alarmExpanded, setAlarmExpanded] = useState<boolean>(false);
  const [driverExpanded, setDriverExpanded] = useState<boolean>(false);

  const [cisNumberExpanded, setCisNumberExpanded] = useState<boolean>(false);
  const [oprsNumberExpanded, setOprsNumberExpanded] = useState<boolean>(false);
  const [tripIdExpanded, setTripIdExpanded] = useState<boolean>(false);
  const [productTypeExpanded, setProductTypeExpanded] = useState<boolean>(false);
  const [stageNumberExpanded, setStageNumberExpanded] = useState<boolean>(false);
  const [tripStartCityExpanded, setTripStartCityExpanded] = useState<boolean>(false);
  const [tripEndCityExpanded, setTripEndCityExpanded] = useState<boolean>(false);

  // Handlers
  const handleGroupChange = (newValue: string) => {
    setSelectedGroup(newValue);
  };

  const handleTripStatusChange = (newValue: string) => {
    setSelectedTripStatus(newValue);
  };

  const handleVehicleChange = (newValue: string) => {
    setSelectedVehicle(newValue);
  };

  const handleAlarmChange = (newValue: string) => {
    setSelectedAlarm(newValue);
  };

  const handleDriverChange = (newValue: string) => {
    setSelectedDriver(newValue);
  };

  const toggleDataTableExpanded = () => {
    setDataTableExpanded((prev) => !prev);
  };

  const toggleTripStatusExpanded = () => {
    setTripStatusExpanded((prev) => !prev);
  };

  const toggleVehicleExpanded = () => {
    setVehicleExpanded((prev) => !prev);
  };
  const toggleAlarmExpanded = () => {
    setAlarmExpanded((prev) => !prev);
  };

  const toggleDriverExpanded = () => {
    setDriverExpanded((prev) => !prev);
  };

  const toggleCisNumberExpanded = () => {
    setCisNumberExpanded((prev) => !prev);
  };

  const toggleOprsNumberExpanded = () => {
    setOprsNumberExpanded((prev) => !prev);
  };

  const toggleTripIdExpanded = () => {
    setTripIdExpanded((prev) => !prev);
  };

  const toggleProductTypeExpanded = () => {
    setProductTypeExpanded((prev) => !prev);
  };

  const toggleStageNumberExpanded = () => {
    setStageNumberExpanded((prev) => !prev);
  };

  const toggleTripStartCityExpanded = () => {
    setTripStartCityExpanded((prev) => !prev);
  };

  const toggleTripEndCityExpanded = () => {
    setTripEndCityExpanded((prev) => !prev);
  };

  const handleReset = () => {
    setSelectedGroup(null);
    setSelectedTripStatus(null);
    setSelectedVehicle(null);
    setSelectedAlarm(null);
    setSelectedDriver(null);

    setCisNumber(null);
    setOprsNumber(null);
    setTripId(null);
    setProductType(null);
    setStageNumber(null);
    setTripStartCity(null);
    setTripEndCity(null);

    onReset?.();
  };

  const handleSave = () => {
    // Perform save logic (send selected filters to parent or API)
    console.log('Filters Applied:', {
      group: selectedGroup,
      tripStatus: selectedTripStatus,
      vehicle: selectedVehicle,
      alarm: selectedAlarm,
      driver: selectedDriver,
    });
    handleFilterSave({
      group: selectedGroup,
      tripStatus: selectedTripStatus,
      vehicle: selectedVehicle,
      alarm: selectedAlarm,
      driver: selectedDriver,

      cisNumber,
      oprsNumber,
      tripId,
      productType,
      stageNumber,
      tripStartCity,
      tripEndCity,
    });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Initialize selections from initialFilters when opening
  useEffect(() => {
    if (open) {
      if (initialFilters) {
        setSelectedGroup(initialFilters.group ?? null);
        setSelectedTripStatus(initialFilters.tripStatus ?? null);
        setSelectedVehicle(initialFilters.vehicle ?? null);
        setSelectedAlarm(initialFilters.alarm ?? null);
        setSelectedDriver(initialFilters.driver ?? null);

        setCisNumber(initialFilters?.cisNumber ?? null);
        setOprsNumber(initialFilters?.oprsNumber ?? null);
        setTripId(initialFilters?.tripId ?? null);
        setProductType(initialFilters?.productType ?? null);
        setStageNumber(initialFilters?.stageNumber ?? null);
        setTripStartCity(initialFilters?.tripStartCity ?? null);
        setTripEndCity(initialFilters?.tripEndCity ?? null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasChanges = useMemo(() => {
    const baseline = initialFilters || {};
    const eq = (a: string | null | undefined, b: string | null | undefined) => (a ?? null) === (b ?? null);

    // If trgtcData is true, check all fields
    if (trgtcData) {
      return !(
        eq(selectedGroup, baseline.group) &&
        eq(selectedTripStatus, baseline.tripStatus) &&
        eq(selectedVehicle, baseline.vehicle) &&
        eq(selectedAlarm, baseline.alarm) &&
        eq(selectedDriver, baseline.driver) &&
        eq(cisNumber, baseline.cisNumber) &&
        eq(oprsNumber, baseline.oprsNumber) &&
        eq(tripId, baseline.tripId) &&
        eq(productType, baseline.productType) &&
        eq(stageNumber, baseline.stageNumber) &&
        eq(tripStartCity, baseline.tripStartCity) &&
        eq(tripEndCity, baseline.tripEndCity)
      );
    }

    // Default logic (for non-trgtcData screens)
    return !(
      eq(selectedGroup, baseline.group) &&
      eq(selectedTripStatus, baseline.tripStatus) &&
      eq(selectedVehicle, baseline.vehicle) &&
      eq(selectedAlarm, baseline.alarm) &&
      eq(selectedDriver, baseline.driver)
    );
  }, [
    selectedGroup,
    selectedTripStatus,
    selectedVehicle,
    selectedAlarm,
    selectedDriver,
    cisNumber,
    oprsNumber,
    tripId,
    productType,
    stageNumber,
    tripStartCity,
    tripEndCity,
    initialFilters,
    trgtcData,
  ]);

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="customise-table-title"
      aria-describedby="customise-table-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: 'auto',
          maxHeight: '80vh',
          minHeight: '80vh',
        }}
      >
        <div className="flex justify-between  h-full flex-col">
          <div className="flex gap-8 flex-col">
            {/* Header */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <Typography variant="h6">
                  {title} {subTitle}
                </Typography>

                {showClearButton && onReset && (
                  <CustomIconButton
                    text="Clear All"
                    icon={<img src={refresh} alt="reset" />}
                    background="transparent"
                    variant="outlined"
                    onClick={handleReset}
                  />
                )}
              </div>

              {description && (
                <Typography variant="button" fontWeight={400} color="secondary">
                  {description}
                </Typography>
              )}
            </div>
            {/* Groups section */}
            {showGroupFilter && (
              <div className="flex flex-col gap-2">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={toggleDataTableExpanded}
                >
                  <Typography variant="body1">{trgtcData ? 'Depot' : 'Groups'}</Typography>

                  <img
                    src={dataTableExpanded ? arrowUp : arrowDown}
                    width={24}
                    height={24}
                    alt={dataTableExpanded ? 'collapse' : 'expand'}
                  />
                </div>

                {dataTableExpanded && (
                  <div className="max-w-[50%]">
                    <CustomDropDownInput
                      options={groupOptions.map(({ code, label }) => ({
                        value: code,
                        label,
                      }))}
                      value={selectedGroup || ''}
                      onChange={handleGroupChange}
                      placeholder={trgtcData ? 'Select Depot' : 'Select Group'}
                    />
                  </div>
                )}
              </div>
            )}
            {/* {showTripStatus && <hr />} */}
            {/* Trip Status section */}
            {showTripStatus && (
              <div className="flex flex-col gap-2">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={toggleTripStatusExpanded}
                >
                  <Typography variant="body1">Trip Status</Typography>

                  <img
                    src={tripStatusExpanded ? arrowUp : arrowDown}
                    width={24}
                    height={24}
                    alt={tripStatusExpanded ? 'collapse' : 'expand'}
                  />
                </div>

                {tripStatusExpanded && (
                  <div className="max-w-[50%]">
                    <CustomDropDownInput
                      options={tripStatusOptions.map(({ code, label }) => ({
                        value: code,
                        label,
                      }))}
                      value={selectedTripStatus || ''}
                      onChange={handleTripStatusChange}
                      placeholder="Select Trip Status"
                    />
                  </div>
                )}
              </div>
            )}
            {/* <hr /> */}
            {/* Vehicle section */}
            {showVehicleFilter && (
              <div className="flex flex-col gap-2">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={toggleVehicleExpanded}
                >
                  <Typography variant="body1">Vehicle</Typography>

                  <img
                    src={vehicleExpanded ? arrowUp : arrowDown}
                    width={24}
                    height={24}
                    alt={vehicleExpanded ? 'collapse' : 'expand'}
                  />
                </div>

                {vehicleExpanded && (
                  <div className="max-w-[50%]">
                    <CustomDropDownInput
                      options={displayedVehicleOptions.map(({ code, label }) => ({
                        value: code,
                        label,
                      }))}
                      value={selectedVehicle || ''}
                      onChange={handleVehicleChange}
                      placeholder="Select Vehicle"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Cis Number section */}
            {showCisNumberFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleCisNumberExpanded}
                  >
                    <Typography variant="body1">CIS Number</Typography>
                    <img src={cisNumberExpanded ? arrowUp : arrowDown} width={24} />
                  </div>

                  {cisNumberExpanded && (
                    <div className="max-w-[50%]">
                      <InputField
                        name="cisNumber"
                        label="CIS Number"
                        value={cisNumber || ''}
                        onChange={(e) => setCisNumber(e.target.value)}
                        placeholder="Enter Cis Number"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {showOprsNumberFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleOprsNumberExpanded}
                  >
                    <Typography variant="body1">OPRS Number</Typography>
                    <img src={oprsNumberExpanded ? arrowUp : arrowDown} width={24} />
                  </div>

                  {oprsNumberExpanded && (
                    <div className="max-w-[50%]">
                      <InputField
                        name="oprsNumber"
                        label="OPRS Number"
                        value={oprsNumber || ''}
                        onChange={(e) => setOprsNumber(e.target.value)}
                        placeholder="Enter OPRS Number"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {showTripIdFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleTripIdExpanded}
                  >
                    <Typography variant="body1">Trip ID</Typography>
                    <img src={tripIdExpanded ? arrowUp : arrowDown} width={24} />
                  </div>

                  {tripIdExpanded && (
                    <div className="max-w-[50%]">
                      <InputField
                        name="tripId"
                        label="Trip ID"
                        value={tripId || ''}
                        onChange={(e) => setTripId(e.target.value)}
                        placeholder="Enter Trip ID"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {showProductTypeFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleProductTypeExpanded}
                  >
                    <Typography variant="body1">Product Type</Typography>
                    <img src={productTypeExpanded ? arrowUp : arrowDown} width={24} />
                  </div>

                  {productTypeExpanded && (
                    <div className="max-w-[50%]">
                      <InputField
                        name="productType"
                        label="Product Type"
                        value={productType || ''}
                        onChange={(e) => setProductType(e.target.value)}
                        placeholder="Enter Product Type"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {showStageNumberFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleStageNumberExpanded}
                  >
                    <Typography variant="body1">Stage Number</Typography>
                    <img src={stageNumberExpanded ? arrowUp : arrowDown} width={24} />
                  </div>

                  {stageNumberExpanded && (
                    <div className="max-w-[50%]">
                      <InputField
                        name="stageNumber"
                        label="Stage Number"
                        value={stageNumber || ''}
                        onChange={(e) => setStageNumber(e.target.value)}
                        placeholder="Enter Stage Number"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {showTripStartCityFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleTripStartCityExpanded}
                  >
                    <Typography variant="body1">Trip Start City</Typography>
                    <img src={tripStartCityExpanded ? arrowUp : arrowDown} width={24} />
                  </div>

                  {tripStartCityExpanded && (
                    <div className="max-w-[50%]">
                      <InputField
                        name="tripStartCity"
                        label="Trip Start City"
                        value={tripStartCity || ''}
                        onChange={(e) => setTripStartCity(e.target.value)}
                        placeholder="Enter Trip Start City"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {showTripEndCityFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleTripEndCityExpanded}
                  >
                    <Typography variant="body1">Trip End City</Typography>
                    <img src={tripEndCityExpanded ? arrowUp : arrowDown} width={24} />
                  </div>

                  {tripEndCityExpanded && (
                    <div className="max-w-[50%]">
                      <InputField
                        name="tripEndCity"
                        label="Trip End City"
                        value={tripEndCity || ''}
                        onChange={(e) => setTripEndCity(e.target.value)}
                        placeholder="Enter Trip End City"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {showDriverFilter && (
              <>
                {/* <hr /> */}
                <div className="flex flex-col gap-2">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={toggleDriverExpanded}
                  >
                    <Typography variant="body1">Driver Name</Typography>
                    <img
                      src={driverExpanded ? arrowUp : arrowDown}
                      width={24}
                      height={24}
                      alt={driverExpanded ? 'collapse' : 'expand'}
                    />
                  </div>
                  {driverExpanded && (
                    <div className="max-w-[50%]">
                      <CustomDropDownInput
                        options={driverOptions}
                        value={selectedDriver || ''}
                        onChange={handleDriverChange}
                        placeholder="Select Driver Name"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* {showAlarmFilter && <hr />} */}
            {/* Alarm Type section */}
            {showAlarmFilter && (
              <div className="flex flex-col gap-2">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={toggleAlarmExpanded}
                >
                  <Typography variant="body1">Alarm Type</Typography>

                  <img
                    src={alarmExpanded ? arrowUp : arrowDown}
                    width={24}
                    height={24}
                    alt={alarmExpanded ? 'collapse' : 'expand'}
                  />
                </div>

                {alarmExpanded && (
                  <div className="max-w-[50%]">
                    <CustomDropDownInput
                      options={alarmOptions}
                      value={selectedAlarm || ''}
                      onChange={handleAlarmChange}
                      placeholder="Select Alarm"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 items-center  justify-between">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline
              padding="0px"
              variant="outlined"
              onClick={handleCancel}
            />
            <CustomIconButton
              text="Apply"
              outline
              padding="0px"
              variant="contained"
              isDisabled={!hasChanges}
              onClick={handleSave}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default FilterDataModel;
